/**
 * Feature flags for this deployment, set per subscription in the license service and served by
 * GET /v1/feature/flags (api/utils/feature_utils.py). Nothing here
 * knows any feature — a disabled flag's scope.routes and scope.components say what to hide.
 * The backend enforces the same flags, so this only keeps dead screens away; a failed read
 * hides nothing.
 */
import api from '@/utils/api';
import { getAuthorization } from '@/utils/authorization-util';
import notification from '@/utils/notification-util';

export type FeatureScope = {
  api?: string[];
  components?: string[];
  routes?: string[];
};

export type FeatureFlag = {
  name: string;
  enabled: boolean;
  scope: FeatureScope | null;
};

const TTL_MS = 60 * 1000;
let cached: { at: number; promise: Promise<FeatureFlag[]> } | null = null;

// Only lists of strings survive: a scope value arriving as a bare string would break .some() while the
// sidebar renders.
const cleanScope = (raw: unknown): FeatureScope | null => {
  if (!raw || typeof raw !== 'object') return null;
  const scope: FeatureScope = {};
  (['api', 'components', 'routes'] as const).forEach((key) => {
    const value = (raw as Record<string, unknown>)[key];
    if (Array.isArray(value)) {
      const entries = value.filter(
        (e): e is string => typeof e === 'string' && e.length > 0,
      );
      if (entries.length) scope[key] = entries;
    }
  });
  return Object.keys(scope).length ? scope : null;
};

const cleanFlags = (data: unknown): FeatureFlag[] =>
  Array.isArray(data)
    ? data
        .filter((f) => f && typeof f.name === 'string')
        .map((f) => ({
          name: f.name,
          enabled: f.enabled === true,
          scope: cleanScope(f.scope),
        }))
    : [];

/** Drop the cached read, so the next loadFeatureFlags() asks the API (an explicit refresh). */
export function clearFeatureFlagCache() {
  cached = null;
}

export function loadFeatureFlags(): Promise<FeatureFlag[]> {
  const authorization = getAuthorization();
  if (!authorization) {
    return Promise.resolve([]);
  }
  if (cached && Date.now() - cached.at < TTL_MS) {
    return cached.promise;
  }
  const promise = fetch(api.feature_flags, {
    headers: { Authorization: authorization },
  })
    .then((res) => res.json())
    .then((body) => (body?.code === 0 ? cleanFlags(body.data) : []))
    .catch(() => []);
  cached = { at: Date.now(), promise };
  return promise;
}

// Where a blocked page sends the user, so it can never itself be blocked: that would loop.
const LANDING_PAGE = '/home';
// Signing in: never blocked, or nobody could reach Discover at all.
const SIGN_IN_PAGES = ['/login', '/login-next'];

// A bare "/" (or empty) entry would cover every page; it is ignored, and the licensing service refuses it.
const coversPath = (path: string, prefix: string) => {
  const base = prefix.replace(/\/+$/, '');
  return base !== '' && (path === base || path.startsWith(`${base}/`));
};

// Pages that moved, and the address a flag's scope.routes may still name them by: a flag defined
// before the move must keep covering the page.
const MOVED_PAGES: [string, string][] = [['/reportstudio', '/report']];

/** The page's address, and its old one when it moved. */
const addressesOf = (path: string) => [
  path,
  ...MOVED_PAGES.filter(([now]) => coversPath(path, now)).map(
    ([now, before]) => before + path.slice(now.length),
  ),
];

/** The disabled flag whose scope.routes cover this page, or undefined. */
export const routeBlockedBy = (
  flags: FeatureFlag[],
  path: string,
): string | undefined =>
  path === LANDING_PAGE || SIGN_IN_PAGES.some((p) => coversPath(path, p))
    ? undefined
    : flags.find(
        (f) =>
          !f.enabled &&
          (f.scope?.routes ?? []).some((p) =>
            addressesOf(path).some((a) => coversPath(a, p)),
          ),
      )?.name;

export const isRouteBlocked = (flags: FeatureFlag[], path: string) =>
  routeBlockedBy(flags, path) !== undefined;

/**
 * Says why a page just redirected home. A blocked page is often opened from outside Discover — a
 * link, or a chat client handing off an @report — so a silent landing on /home reads as a bug.
 * Keyed by flag, so the route wrapper and onRouteChange both redirecting shows it once.
 */
export function notifyFeatureOff(featureName: string) {
  const label = featureName.replace(/[_-]+/g, ' ').trim();
  notification.warning({
    key: `feature-off-${featureName}`,
    message: `${label.charAt(0).toUpperCase()}${label.slice(1)} is turned off`,
    description:
      'This feature is not enabled on this deployment. Ask your administrator to turn it on.',
  });
}

export const isComponentBlocked = (flags: FeatureFlag[], name: string) =>
  flags.some((f) => !f.enabled && (f.scope?.components ?? []).includes(name));
